// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — where on Earth a dataset has data (`Dataset.boundingBox`), for
 * Browse's Map view and its region filter.
 *
 * The legacy SOS catalogue and the stream index carried none, so the
 * Map view said "302 datasets have no geographic coverage" (owner,
 * 2026-10-03). What each source does give:
 *   - **Streams:** the index row's `coverage` (`global` | `regional`)
 *     and, for a regional row, its `region` slug. The `.dsa` schema has
 *     a place for exact bounds (`layers[].geo.bounds`), but none of the
 *     155 published files filled it on 2026-10-03, and the `.dsa` is
 *     only fetched on load anyway; bounds on the index row (`bounds`,
 *     or `geo.bounds`, west/south/east/north) win when the stream host
 *     starts sending them. Otherwise a region slug maps to its box here.
 *   - **SOS rows:** `boundingVariables` (n/s/e/w as strings) on a few
 *     rows; every other Earth dataset in the catalogue is a whole-globe
 *     picture, so worldwide.
 *   - **Not Earth** (the Moon, planets, the Sun streams) and stories: no
 *     box, so the Map leaves them out rather than drawing them on Earth.
 *
 * Pure: no DOM, no network.
 */

export interface Bounds {
  n: number
  s: number
  e: number
  w: number
}

export const WORLD: Readonly<Bounds> = { n: 90, s: -90, e: 180, w: -180 }

/**
 * The stream host's region slugs (index schema 1.2+), as lon/lat boxes
 * a little wider than the land they cover. A new slug falls back to
 * nothing: better absent from the Map than drawn in the wrong place.
 */
const REGIONS: Readonly<Record<string, Bounds>> = {
  // All of Spain: the peninsula, the Balearic and Canary Islands, Ceuta
  // and Melilla. The AEMET rows' own box since the Canary Islands rows
  // were merged into them (2026-10-30), so a region picked on the Map
  // keeps them (a named region keeps only boxes inside it).
  spain: { n: 44.4875, s: 25.4875, w: -20.0125, e: 4.9875 },
  conus: { n: 49.5, s: 24.3, w: -125.0, e: -66.9 },
  netherlands: { n: 53.7, s: 50.7, w: 3.3, e: 7.3 },
  europe: { n: 72.0, s: 34.0, w: -25.0, e: 45.0 },
  'north-america': { n: 72.0, s: 14.0, w: -170.0, e: -50.0 },
}

/** Celestial bodies the globe draws as Earth (the SOS `celestialBody` field). */
const EARTH_BODIES = new Set(['', 'earth', 'aurora'])

const num = (v: unknown): number => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN)

/** A box from `{n,s,e,w}` or `{north,south,east,west}`, numbers or numeric strings; null when not a sane box. */
export function boundsFrom(raw: unknown): Bounds | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const b = {
    n: num(r.n ?? r.north),
    s: num(r.s ?? r.south),
    e: num(r.e ?? r.east),
    w: num(r.w ?? r.west),
  }
  if (![b.n, b.s, b.e, b.w].every(Number.isFinite)) return null
  if (b.n > 90 || b.s < -90 || b.n < b.s || Math.abs(b.e) > 180 || Math.abs(b.w) > 180) return null
  return b
}

/** What a stream's index row says about where it has data. */
export interface StreamCoverageFields {
  coverage?: string
  region?: string
  regionName?: string
  display_name?: string
  name?: string
  bounds?: unknown
  geo?: { bounds?: unknown }
}

/** A stream's box: published bounds, else its region's, else the world for a global stream. */
export function streamBounds(entry: StreamCoverageFields): Bounds | undefined {
  const published = boundsFrom(entry.bounds) ?? boundsFrom(entry.geo?.bounds)
  if (published) return published
  if (entry.coverage === 'global') return { ...WORLD }
  if (entry.coverage !== 'regional') return undefined
  const region = REGIONS[(entry.region ?? '').toLowerCase()]
  return region ? { ...region } : undefined
}

/** What an SOS row says about where it has data. */
export interface SosCoverageFields {
  format?: string
  celestialBody?: string
  boundingVariables?: unknown
}

/** An SOS row's box: its own bounds, else the world for an Earth dataset; none for other bodies and stories. */
export function sosBounds(row: SosCoverageFields): Bounds | undefined {
  if (row.format === 'khipu/story') return undefined
  if (!EARTH_BODIES.has((row.celestialBody ?? '').trim().toLowerCase())) return undefined
  return boundsFrom(row.boundingVariables) ?? { ...WORLD }
}

/** Where the camera should go for a dataset's box (`focusFor`). */
export type RegionFocus =
  /** Fit this box ([west, south, east, north]); `lat`/`lon` is its middle. */
  | { kind: 'box'; bounds: [number, number, number, number]; lat: number; lon: number }
  /** A band all the way round (an Arctic or mid-latitude field): tilt to its middle latitude only. */
  | { kind: 'band'; lat: number }

/**
 * AYNI — where a dataset with data over only part of the Earth should
 * open: on its region, so the viewer is not left looking at empty globe.
 * Null for the whole Earth, or no box. A box that wraps the antimeridian
 * (west > east) is handled; its middle longitude is wrapped to ±180.
 */
export function focusFor(b: Bounds | null | undefined): RegionFocus | null {
  if (!b) return null
  const lonSpan = b.w <= b.e ? b.e - b.w : 360 - b.w + b.e
  const latSpan = b.n - b.s
  const lat = (b.n + b.s) / 2
  if (lonSpan >= 359) return latSpan >= 170 ? null : { kind: 'band', lat }
  let lon = b.w + lonSpan / 2
  if (lon > 180) lon -= 360
  return { kind: 'box', bounds: [b.w, b.s, b.e, b.n], lat, lon }
}

/**
 * AYNI — the stream host's region slug whose box this is, or null: the
 * Browse panel names a region filter set by clicking a region on the
 * Map ("Spain") instead of printing its corners. Exact to 0.001°, which
 * is how the filter's URL form rounds.
 */
export function regionSlugFor(b: Bounds | null | undefined): string | null {
  if (!b) return null
  const near = (x: number, y: number) => Math.abs(x - y) < 0.0011
  for (const [slug, r] of Object.entries(REGIONS)) {
    if (near(r.n, b.n) && near(r.s, b.s) && near(r.e, b.e) && near(r.w, b.w)) return slug
  }
  return null
}
